import * as React from 'react';
import {SemanticICONS} from 'semantic-ui-react';

import * as Generated from '../generated/graphql';
import * as GQLTypes from '../types/graphql';
import {TagAddButton} from './TagAddButton';
import {Tag} from './Tags';

export interface TagAttributes {
  color?: number;
  icon?: SemanticICONS;
}

export type ModifiedTag = {
  id: string;
  name: string;
  tagCategoryName: string;
  attributes: TagAttributes;
};

export function createTag(tag: ModifiedTag): string {
  return tag.tagCategoryName + ':' + tag.name;
}

export function parseTag(tag: string): [string | null, string] {
  // Function to parse tag to get [tagCategoryName, tagName]
  const sliceIdx = tag.indexOf(':');
  const tagCategoryName = sliceIdx === -1 ? null : tag.slice(0, sliceIdx);
  const tagName = tagCategoryName === null ? tag : tag.slice(sliceIdx + 1);
  return [tagCategoryName, tagName];
}

export function matchTag(tag: ModifiedTag, target: string): boolean {
  const fullTag = tag.tagCategoryName + ':' + tag.name;
  return (
    fullTag.toLowerCase() === target.toLowerCase() ||
    tag.name.toLowerCase() === target.toLowerCase()
  );
}

export function mapGQLTags(
  rawTags: Array<Record<string, string>> | Generated.Tag[]
): ModifiedTag[] {
  // Tags are retrieved via a mix of weave/generated queries,
  // so the Type is either a data dictionary or a GQL Type, hence any
  return rawTags.map((t: any) => {
    const tag = t as any;
    return {
      id: tag.id,
      name: tag.name,
      tagCategoryName: tag.tagCategoryName,
      attributes: parseTagAttributes(tag.attributes),
    } as ModifiedTag;
  });
}

export function parseTagAttributes(attributes: string): TagAttributes {
  try {
    const parsedAttributes = JSON.parse(attributes);
    return {
      color: parsedAttributes.color,
      icon: parsedAttributes.icon,
    } as TagAttributes;
  } catch (err) {
    console.error(`Failed to parse tag attributes:`, err);
    return {color: undefined, icon: undefined} as TagAttributes;
  }
}

export function createAttributesString(
  attributes: TagAttributes
): string | undefined {
  try {
    const jsonAttributes = JSON.stringify(attributes);
    const parsedAttributes = parseTagAttributes(jsonAttributes);
    if (
      parsedAttributes.color === attributes.color &&
      parsedAttributes.icon === attributes.icon
    ) {
      return jsonAttributes;
    }
  } catch (err) {
    // Defer to the backend to assign default attributes in case of parse errors
    console.error(`Failed to create tag attributes json string:`, err);
  }
  return undefined;
}

interface TagAssignmentProps {
  tag: ModifiedTag;
  showColor: boolean;
  size: 'small' | 'medium' | 'large';
  canDelete?: boolean;
  onDelete?: () => Promise<any>;
  onClick?(): void;
}

export const TagAssignment = (props: TagAssignmentProps) => {
  const {tag, showColor, size, onDelete, onClick} = props;
  const canDelete = props.canDelete ?? true;
  return (
    <>
      <Tag
        tag={{
          name: tag.name,
          colorIndex: tag.attributes.color,
          icon: tag.attributes.icon,
        }}
        noun={'tag'}
        onDelete={onDelete}
        onClick={onClick}
        size={size}
        showColor={showColor}
        canDelete={canDelete}
      />
    </>
  );
};

interface AddTagAssignmentButtonProps {
  assignedTags: ModifiedTag[];
  availableTags: ModifiedTag[];
  onCreate: (newTag: GQLTypes.Tag) => Promise<any>;
}

function mapModifiedTagList(tags: ModifiedTag[]): GQLTypes.Tag[] {
  return tags.map(t => {
    return {
      name: t.name,
      colorIndex: t.attributes.color,
      icon: t.attributes.icon,
    };
  });
}

export const AddTagAssignmentButton = (props: AddTagAssignmentButtonProps) => {
  const {assignedTags, availableTags, onCreate} = props;
  return (
    <TagAddButton
      tags={mapModifiedTagList(assignedTags)}
      availableTags={mapModifiedTagList(availableTags)}
      addTag={async tag => {
        await onCreate(tag);
      }}
      compact={true}
      direction="bottom right"
      noun={'tag'}
    />
  );
};
